/**
 * PSV-1726 — ship class tree (Starblast-style tier-up).
 *
 * Replaces the linear PSV-1338 progression with a branching tree: at each
 * tier-up the player CHOOSES a class with a distinct character (speed vs
 * armor vs damage vs cargo). Simplified 3-tier MVP tree:
 *
 *   Tier 1:  Fly (start, single option)
 *   Tier 2:  Delta-Fighter (speed/agility)  |  Trident (3 barrels, slow)
 *   Tier 3:  Pioneer (mining/cargo)         |  Crusader (combat, tanky)
 *
 * Shared between client and server — the validating authority (server
 * `ChooseShipClass` RPC) and the cost/stat-rendering UI (class picker
 * modal) import this single source of truth, mirroring `upgrades.ts`.
 *
 * `cargoCapacity` (PSV-1728) is the gem HOLD limit — gems ARE the cargo.
 * Every capacity must fit the class's next purchase (stat level ≤31 gems,
 * tier-2 class 100, tier-3 class 300) or progression deadlocks; the
 * value ships now so class balance doesn't need a second pass later.
 */

export interface WeaponConfig {
  /** Simultaneous projectiles per shot. >1 fans out around the aim angle. */
  barrels: number;
  /** Damage PER PROJECTILE (a 3-barrel volley can out-damage one big shot). */
  damage: number;
  /** Server-enforced cooldown between shots, ms. */
  reloadMs: number;
  /** Angular fan half-spread between outermost barrels, radians. */
  spreadRad: number;
}

/** PSV-1905 — active ability (Shift). Server-authoritative: the server
 * validates class + cooldown and applies the effect; clients render from
 * the AbilityUsed broadcast. */
export interface AbilityDef {
  id: 'dash' | 'shieldwall' | 'drones' | 'snipe' | 'overdrive';
  name: string;
  desc: string;
  cooldownMs: number;
  /** Effect duration where applicable (shieldwall/drones/overdrive/snipe arm window). */
  durationMs?: number;
}

export interface ShipClassDef {
  /**
   * PSV-1905 — DAG edges: ids of the classes this one can be bought
   * FROM. Absent = any class one tier below (legacy t1-t3 behavior).
   * Keeps upgrade paths from reconverging.
   */
  from?: readonly string[];
  /** PSV-1905 — active ability on Shift (T4+). */
  ability?: AbilityDef;
  /** Stable id — persisted in `Player.Ship.Class`, sent on the wire. */
  id: string;
  /** Display name for cards / labels. */
  name: string;
  /** Tier this class belongs to (1-based, matches the Starblast mental model). */
  tier: number;
  /** Gems required to tier-up INTO this class (0 for the starter). */
  cost: number;
  hpMax: number;
  /** Movement speed cap, px/s (server-authoritative; prediction mirrors it). */
  engineMax: number;
  /** Rotation speed, rad/s (per-class replacement for MatchConfig.ShipRotationSpeed). */
  agility: number;
  weapon: WeaponConfig;
  /** PSV-1728 forward-declaration — max crystals carried. */
  cargoCapacity: number;
  /** One-line flavor for the picker card. */
  desc: string;
  /** Placeholder visual until per-class sprites land: tint + scale on the shared ship texture. */
  tint: number;
  scale: number;
}

export const SHIP_CLASSES: readonly ShipClassDef[] = [
  {
    id: 'fly',
    name: 'Fly',
    tier: 1,
    cost: 0,
    hpMax: 100,
    engineMax: 200,
    agility: 8,
    weapon: { barrels: 1, damage: 10, reloadMs: 500, spreadRad: 0 },
    cargoCapacity: 130,
    desc: 'Стартовый разведчик — лёгкий и вертлявый.',
    tint: 0xffffff,
    scale: 1.0,
  },
  {
    id: 'delta-fighter',
    name: 'Delta-Fighter',
    tier: 2,
    cost: 100,
    hpMax: 140,
    engineMax: 260,
    agility: 10,
    weapon: { barrels: 1, damage: 14, reloadMs: 400, spreadRad: 0 },
    cargoCapacity: 340,
    desc: 'Скорость и манёвр — жалит и уходит.',
    tint: 0x66ccff,
    scale: 1.05,
  },
  {
    id: 'trident',
    name: 'Trident',
    tier: 2,
    cost: 100,
    hpMax: 180,
    engineMax: 210,
    agility: 7,
    weapon: { barrels: 3, damage: 9, reloadMs: 600, spreadRad: 0.18 },
    cargoCapacity: 340,
    desc: 'Три ствола веером — медленный, но злой.',
    tint: 0xffaa44,
    scale: 1.15,
  },
  {
    id: 'pioneer',
    name: 'Pioneer',
    tier: 3,
    cost: 300,
    hpMax: 220,
    engineMax: 240,
    agility: 7.5,
    weapon: { barrels: 2, damage: 12, reloadMs: 500, spreadRad: 0.1 },
    cargoCapacity: 520,
    desc: 'Шахтёр — огромный трюм на 520 кристаллов.',
    tint: 0x88ee88,
    scale: 1.2,
  },
  {
    id: 'crusader',
    name: 'Crusader',
    tier: 3,
    cost: 300,
    hpMax: 320,
    engineMax: 220,
    agility: 6.5,
    weapon: { barrels: 2, damage: 18, reloadMs: 550, spreadRad: 0.08 },
    cargoCapacity: 360,
    desc: 'Боевой утюг — толстый и больно бьёт.',
    tint: 0xff6666,
    scale: 1.3,
  },
  // ---------- Tier 4 (PSV-1905) — branch-locked, each with an ability ----------
  {
    id: 'harvester',
    name: 'Harvester',
    tier: 4,
    cost: 500, // fits Pioneer's 520 hold — reachable by mining alone
    from: ['pioneer'],
    hpMax: 300,
    engineMax: 250,
    agility: 7,
    weapon: { barrels: 2, damage: 14, reloadMs: 480, spreadRad: 0.1 },
    cargoCapacity: 950,
    desc: 'Индустриальный шахтёр — дроны копают за тебя.',
    tint: 0x55ffcc,
    scale: 1.3,
    ability: {
      id: 'drones',
      name: 'Mining Drones',
      desc: 'Deploy 2 drones that auto-mine nearby asteroids for 10s',
      cooldownMs: 15_000,
      durationMs: 10_000,
    },
  },
  {
    id: 'bulwark',
    name: 'Bulwark',
    tier: 4,
    cost: 500,
    from: ['pioneer'],
    hpMax: 460,
    engineMax: 190,
    agility: 5.5,
    weapon: { barrels: 3, damage: 12, reloadMs: 650, spreadRad: 0.2 },
    cargoCapacity: 880,
    desc: 'Черепаха с бронёй — лобовой щит глотает снаряды.',
    tint: 0x99aaff,
    scale: 1.4,
    ability: {
      id: 'shieldwall',
      name: 'Shield Wall',
      desc: 'Frontal arc absorbs ALL projectiles for 3s',
      cooldownMs: 12_000,
      durationMs: 3_000,
    },
  },
  {
    id: 'reaper',
    name: 'Reaper',
    tier: 4,
    cost: 350, // fits Crusader's 360 hold
    from: ['crusader'],
    hpMax: 340,
    engineMax: 280,
    agility: 8.5,
    weapon: { barrels: 2, damage: 22, reloadMs: 500, spreadRad: 0.06 },
    cargoCapacity: 880,
    desc: 'Хищник ближнего боя — рывок и добивание.',
    tint: 0xff4488,
    scale: 1.3,
    ability: {
      id: 'dash',
      name: 'Dash',
      desc: 'Instant burst of speed along your heading',
      cooldownMs: 5_000,
    },
  },
  {
    id: 'artillery',
    name: 'Artillery',
    tier: 4,
    cost: 350,
    from: ['crusader'],
    hpMax: 300,
    engineMax: 200,
    agility: 5,
    weapon: { barrels: 1, damage: 26, reloadMs: 700, spreadRad: 0 },
    cargoCapacity: 900,
    desc: 'Дальнобойная пушка — заряженный выстрел прошивает всё.',
    tint: 0xffcc55,
    scale: 1.35,
    ability: {
      id: 'snipe',
      name: 'Charged Shot',
      desc: 'Arm a 3x-damage, 2x-speed shot (fires on your next shot within 3s)',
      cooldownMs: 10_000,
      durationMs: 3_000,
    },
  },
  // ---------- Tier 5 apex (PSV-1905) — one per arena ----------
  {
    id: 'sovereign',
    name: 'Sovereign',
    tier: 5,
    cost: 850, // fits every T4 hold (880-950)
    from: ['harvester', 'bulwark', 'reaper', 'artillery'],
    hpMax: 560,
    engineMax: 240,
    agility: 6,
    weapon: { barrels: 3, damage: 20, reloadMs: 550, spreadRad: 0.12 },
    cargoCapacity: 1200,
    desc: 'Флагман — один на арену. Убей его ради мегабаунти.',
    tint: 0xffe066,
    scale: 1.55,
    ability: {
      id: 'overdrive',
      name: 'Overdrive',
      desc: '+50% damage and speed for 5s',
      cooldownMs: 20_000,
      durationMs: 5_000,
    },
  },
] as const;

/** The starter class every fresh ship spawns as. */
export const STARTER_CLASS_ID = 'fly';

/** Highest tier in the tree. */
export const MAX_CLASS_TIER = SHIP_CLASSES.reduce(
  (max, c) => Math.max(max, c.tier),
  1,
);

const BY_ID = new Map(SHIP_CLASSES.map((c) => [c.id, c]));

/**
 * Class lookup tolerant of legacy/corrupt records: unknown ids (including
 * the pre-tree `"Scout"` default) fall back to the starter class rather
 * than crashing the spawn path.
 */
export function classById(id: unknown): ShipClassDef {
  if (typeof id === 'string') {
    const def = BY_ID.get(id);
    if (def !== undefined) return def;
  }
  return BY_ID.get(STARTER_CLASS_ID)!;
}

/**
 * The classes offered when tiering up FROM `currentTier` (i.e. the
 * `currentTier + 1` row of the tree). Empty at the top of the tree.
 */
export function nextTierChoices(currentTier: number): ShipClassDef[] {
  return SHIP_CLASSES.filter((c) => c.tier === currentTier + 1);
}

/**
 * PSV-1905 — DAG-aware picker feed: the next-tier classes reachable FROM
 * `currentClassId` (edges keep paths from reconverging; a def without
 * `from` accepts any parent one tier below — legacy t1-t3 behavior).
 */
export function nextChoicesFor(currentClassId: string): ShipClassDef[] {
  const current = classById(currentClassId);
  return SHIP_CLASSES.filter(
    (c) =>
      c.tier === current.tier + 1 &&
      (c.from === undefined || c.from.includes(current.id)),
  );
}

/**
 * Server-side tier-up validation (mirrored client-side for optimistic UI).
 * Returns null when the transition is legal, otherwise a stable reason
 * string the server echoes in its rejection broadcast.
 */
export function validateClassChoice(
  currentClassId: string,
  gems: number,
  targetClassId: string,
): string | null {
  const current = classById(currentClassId);
  const target = BY_ID.get(targetClassId);
  if (target === undefined) return 'unknown class';
  if (target.tier !== current.tier + 1) return 'class not in the next tier';
  // PSV-1905 — respect the DAG edges: you can only buy a child of YOUR node.
  if (target.from !== undefined && !target.from.includes(current.id)) {
    return 'class not reachable from your ship';
  }
  if (gems < target.cost) return 'not enough gems';
  return null;
}
